import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "@/hooks/use-business";

export interface ProductOrderItem {
  id: string;
  order_id: string;
  product_id: string | null;
  product_name: string;
  unit_price: number;
  quantity: number;
  line_total: number;
  notes: string | null;
  created_at: string;
}

export interface ProductOrder {
  id: string;
  business_id: string;
  confirmation_code: string;
  customer_name: string;
  customer_phone: string | null;
  customer_email: string | null;
  fulfillment_type: "pickup" | "delivery";
  delivery_address: string | null;
  delivery_place_id: string | null;
  delivery_lat: number | null;
  delivery_lng: number | null;
  notes: string | null;
  status: string;
  total_amount: number;
  currency: string;
  created_at: string;
  updated_at: string;
  items?: ProductOrderItem[];
}

export function useProductOrders() {
  const { business } = useBusiness();
  const [orders, setOrders] = useState<ProductOrder[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    if (!business?.id) return;
    setLoading(true);
    try {
      const { data: ordersData, error: ordersErr } = await (supabase as any)
        .from("product_orders")
        .select(`
          *,
          items:product_order_items(*)
        `)
        .eq("business_id", business.id)
        .order("created_at", { ascending: false });
      if (ordersErr) throw ordersErr;
      setOrders(ordersData || []);
    } catch (e) {
      console.error("[useProductOrders] fetch failed", e);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [business?.id]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateOrderStatus = async (orderId: string, status: string): Promise<void> => {
    const { error } = await (supabase as any)
      .from("product_orders")
      .update({ status, updated_at: new Date().toISOString() })
      .eq("id", orderId);
    if (error) throw error;
    await fetchOrders();
  };

  return { orders, loading, refetch: fetchOrders, updateOrderStatus };
}
